import { useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { usePatient } from '@hooks/usePatient.ts';
import { useJawsScanSummary } from '@hooks/useJawsScan.ts';
import { useIsAdmin } from '@hooks/useAdmin.ts';
import logo from '/Avaros_favicon.svg';
import UserDropdown from './UserDropdown.tsx';

interface RouteConfig {
  pattern: string;
  label: string;
  showBack?: boolean;
  getBackPath?: (
    params: Record<string, string>,
    context: {
      patientId?: string;
      scanId?: string;
    },
  ) => string | undefined;
}

const defaultBackPath = (_: Record<string, string>, context: { patientId?: string; scanId?: string }) => {
  if (context.patientId && context.scanId) {
    return `/patients/${context.patientId}/scans/${context.scanId}`;
  }
  if (context.patientId) {
    return `/patients/${context.patientId}`;
  }
  return '/patients';
};

const ROUTE_CONFIGS: RouteConfig[] = [
  {
    pattern: '/patients/:patientId/scans/:scanId/segmentation',
    label: 'Segmentation',
    showBack: true,
    getBackPath: defaultBackPath,
  },
  {
    pattern: '/patients/:patientId/scans/:scanId/correction',
    label: 'Correction',
    showBack: true,
    getBackPath: defaultBackPath,
  },
  {
    pattern: '/patients/:patientId/scans/:scanId/export',
    label: 'Export',
    showBack: true,
    getBackPath: defaultBackPath,
  },
];

function deriveLabelFromPath(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return 'Workspace';
  }
  const lastSegment = segments[segments.length - 1];
  return lastSegment
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function ThreeSceneTopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();

  const routeMatch = useMemo(() => {
    for (const config of ROUTE_CONFIGS) {
      const match = matchPath({ path: config.pattern, end: true }, location.pathname);
      if (match) {
        return { config, params: match.params as Record<string, string> };
      }
    }
    return undefined;
  }, [location.pathname]);

  const matchedConfig = routeMatch?.config;
  const routeParams = routeMatch?.params ?? {};

  const label = matchedConfig?.label ?? deriveLabelFromPath(location.pathname);
  const showBack = matchedConfig?.showBack ?? false;

  const routePatientId = routeParams.patientId;
  const routeScanId = routeParams.scanId;

  const scanSummaryQuery = useJawsScanSummary(routeScanId ?? '', {
    enabled: Boolean(routeScanId),
  });

  const patientId = scanSummaryQuery.data?.patientId ?? routePatientId;

  const patientQuery = usePatient(patientId ?? '', {
    enabled: Boolean(patientId),
  });

  const patientName = patientQuery.data ? `${patientQuery.data.firstName} ${patientQuery.data.lastName}` : undefined;

  // A platform role has no clinic dashboard to fall back to — it came in from the
  // console's case panel, so that is where "back" belongs.
  const consoleBackPath =
    isAdmin && patientId
      ? `/admin/pipeline?casePatient=${patientId}${routeScanId ? `&caseScan=${routeScanId}` : ''}`
      : undefined;

  const fallbackBackPath =
    consoleBackPath ??
    matchedConfig?.getBackPath?.(routeParams, {
      patientId,
      scanId: routeScanId,
    });

  const isPatientLoading =
    (Boolean(routeScanId) && scanSummaryQuery.isLoading) || (Boolean(patientId) && patientQuery.isLoading);

  const handleBack = () => {
    const canGoBack = typeof window !== 'undefined' && window.history?.state && typeof window.history.state.idx === 'number' && window.history.state.idx > 0;

    if (canGoBack) {
      navigate(-1);
      return;
    }

    if (fallbackBackPath) {
      navigate(fallbackBackPath);
      return;
    }

    navigate(isAdmin ? '/admin/pipeline' : '/patients');
  };

  return (
    <header className="relative z-[1000] grid w-300 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 rounded-3xl border border-primary/40 bg-gradient-to-r from-primary/35 via-primary/10 to-primary/15 px-6 py-3 text-white shadow-xl shadow-primary/20 backdrop-blur-lg">
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/80 via-primary/60 to-primary/30 shadow-inner shadow-primary/40">
          <img src={logo} className="h-7 w-7" alt="Zubee Factory logo" />
        </div>
        <div className="space-y-0.5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/60">Zubee Factory</p>
            <h1 className="text-lg font-semibold tracking-tight">Orthodontist Workspace</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {showBack && (
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.35em] text-white/70 transition hover:border-white/40 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              >
                <ArrowLeft className="h-3 w-3" />
                <span>Back</span>
              </button>
            )}
            <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-white">{label}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center text-center text-white">
        <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-white/70">Patient</span>
        <span className="text-sm font-semibold tracking-tight text-white">
          {isPatientLoading ? 'Loading…' : patientName ?? 'Unknown'}
        </span>
      </div>

      <div className="flex items-center gap-2 justify-self-end">
        <UserDropdown />
      </div>
    </header>
  );
}
